<template>
    <div class="subscription-form">
        <template v-if="!isSended">
            <z-caption class="u-bottom-margin--s-important" tag="h2" size="lt">
                <span v-html="formTitle"></span>
            </z-caption>
            <form class="subscription-form__form" @submit.prevent>
                <div>
                    <z-input
                        type="email"
                        :placeholder="text.inputPlaceholder"
                        v-model="params.email"
                        ref="refEmail"
                        required
                        :disabled="state === 'edit'"
                    />
                </div>
                <div class="u-top-margin--m" v-if="isShowNewsSubscribes">
                    <p class="subscription-form__group-title">{{ text.news }}: </p>
                    <z-checkbox-group
                        :data="rubricsAdaptive"
                        v-model="params.rubrics"
                        :required="params.state !== 'unsubscribe'"
                        show-all-checkbox
                        :select-all-text="text.allRubrics"
                        @change="isCheckboxError = false"
                    />
                </div>
                <div class="u-top-margin--m" v-if="isShowProgramsSubscribes">
                    <p class="subscription-form__group-title">{{ text.programs }}: </p>
                    <z-checkbox-group
                        :data="programsAdaptive"
                        v-model="params.programs"
                        :required="params.state !== 'unsubscribe'"
                        show-all-checkbox
                        :select-all-text="text.allPrograms"
                        @change="isCheckboxError = false"
                    />
                </div>
                <p class="subscription-form__checkbox-error" v-if="isCheckboxError">{{ text.checkboxError }}</p>
                <div class="u-top-margin--l">
                    <z-checkbox
                        :data="{
                            text: policyText,
                            id: 'agree',
                            selected: false
                        }"
                        required
                        v-model="params.agree"
                        size="s"
                        ref="refAgree"
                    />
                </div>
                <div
                    class="subscription-form__errors"
                    v-if="errors && errors[0]"
                >
                    <div class="subscription-form__errors-item">
                        <z-icon width="16" height="16" name="general/warning" />
                        <vue-raw
                            tag="span"
                            :raw="errorsMap[errors[0]] || text.defaultError"
                        />
                        <template v-if="errors[0] === 'email-14'">
                            <z-link theme="dark" tag="button" @click="onReconfirm">
                                <span v-html="text.reconfirmLink"></span>
                            </z-link>
                        </template>
                    </div>
                </div>
                <div class="subscription-form__actions">
                    <div class="g-recaptcha" ref="recaptchaWidgetEl"></div>
                    <z-button
                        :loading="isLoading"
                        type="button"
                        @click.prevent="onClickSubmitButton"
                    >
                        <span v-html="submitBtnText"></span>
                    </z-button>
                    <z-link
                        tag="button"
                        type="button"
                        size="m"
                        :underlined="false"
                        theme="dark"
                        @click.prevent="onUnsubscribe"
                        v-if="['requestEdit', 'edit', 'requestUnsubscribe'].includes(state)"
                    >
                        <span v-html="text.unsubscribe"></span>
                    </z-link>
                </div>
            </form>
            <vue-raw
                class="text-size-l u-top-margin--s"
                v-if="formDescription"
                :raw="formDescription"
            />
        </template>

        <template v-else>
            <vue-raw class="text-size-l u-bold u-line-height-1_2" :raw="successMessage" />
            <vue-raw class="u-top-margin--s text-size-l" v-if="successDescription" :raw="successDescription" />
        </template>
    </div>
</template>

<script setup>
import { ref, nextTick, onBeforeMount, onMounted, computed } from 'vue'
import { sendSubscription } from '@/api/subscription'
import { useRecaptcha } from '@/composable/useRecaptcha'
import { getGetParams } from '@/utils/filterUtils'
import { useComputedData } from '../composable/useComputedData'
import { text } from '../locales'

const props = defineProps({
    rubrics: {
        type: Array,
        default: () => []
    },
    programs: {
        type: Array,
        default: () => []
    },
    subscriber: {
        type: Object
    },
    state: {
        type: String,
        default: 'subscribe',
        validator: prop => [
            'subscribe',
            'edit',
            'requestEdit',
            'requestUnsubscribe'
        ].includes(prop)
    },
})

const params = ref({
    state: 'subscribe',
    email: '',
    rubrics: [],
    programs: [],
    agree: false
})

const errors = ref([])
const isSended = ref(false)
const isLoading = ref(false)
const isCheckboxError = ref(false)

const refEmail = ref('refEmail')
const refAgree = ref('refAgree')

const { initGRecaptcha, getRecaptchaToken } = useRecaptcha()

const rubricsAdaptive = computed(() => {
    return props.rubrics?.map(item => {
        return {
            ...item,
            group: {
                id: item.lang,
                name: text.langMap[item.lang],
                'all-text': text.allMap[item.lang]
            }
        }
    })
})
const programsAdaptive = computed(() => {
    return props.programs?.map(item => {
        return {
            ...item,
            group: {
                id: item.lang,
                name: text.langMap[item.lang],
                'all-text': text.allPrograms[item.lang]
            }
        }
    })
})
const isShowNewsSubscribes = computed(()=> {
    return ['subscribe', 'edit', 'requestEdit'].includes(props.state) && rubricsAdaptive.value.length
})
const isShowProgramsSubscribes = computed(()=> {
    return ['subscribe', 'edit', 'requestEdit'].includes(props.state) && programsAdaptive.value.length
})


const {
    successMessage,
    successDescription,
    policyText,
    submitBtnText,
    formTitle,
    formDescription,
    errorsMap
} = useComputedData(params, props)

const onSubmit = async () => {
    const isValid = await validateForm()

    if (isValid) {

        isLoading.value = true
        errors.value = []

        try {
            const recaptchaToken = await getRecaptchaToken()

            if (!recaptchaToken) {
                throw new Error('Failed to get reCAPTCHA token');
            }

            params.value.captcha = recaptchaToken
            const response = await sendSubscription(params.value)
            if (response.status === 'success') {
                isSended.value = true
            } else {
                errors.value = response.errors
            }
        } catch (error){
            console.error(error);
        } finally {
            isLoading.value = false
        }
    }
}

const validateForm = async () => {
    let isValid = true
    const requiredFields = [refEmail, refAgree]

    for (let index = 0; index < requiredFields.length; index++) {
        const item = requiredFields[index]
        if (typeof item.value === 'string') continue
        item.value?.validate()
        await nextTick(() => {
            if (item.value?.error !== '') isValid = false
        })
    }

    if(params.value.programs.length === 0 && params.value.rubrics.length === 0){
        isCheckboxError.value = true
        isValid = false
    }
    return isValid
}

const onUnsubscribe = () => {
    params.value.state = props.state === 'requestEdit' ? 'requestUnsubscribe' : 'unsubscribe'
    onSubmit()
}

const onClickSubmitButton = () => {
    params.value.state = props.state
    onSubmit()
}

const onReconfirm = () => {
    params.value.state = 'reconfirm'
    onSubmit()
}

onBeforeMount(() => {
    params.value.state = props.state

    if (props.state === 'edit') {
        params.value.email = props.subscriber?.email
        params.value.id = props.subscriber?.id
        params.value['confirm-code'] = props.subscriber?.['confirm-code']
    }

    if (props.state === 'requestEdit') {
        const { email } = getGetParams();
        params.value.email = email
    }
})
onMounted( () => {
    initGRecaptcha()
})

</script>

<style lang="scss">
.subscription-form {
    --placeholderBg: transparent;

    &__checkbox-error{
        margin: var(--error-top-margin) 0 0;
        font-size: var(--font-size-error);
        font-weight: var(--font-weight-error);
        line-height: var(--line-height-error);
        color: var(--error-text-color);
    }

    &__errors {
        color: var(--error-text-color);
        background: #FFC8C3;
        border-radius: 4px;
        padding: var(--spacer-2xs);
        font-size: var(--error-text-size);
        @include margin-level(top, S);

        .z-icon {
            vertical-align: middle;
            margin-right: 6px;
            margin-top: -0.2em;
        }
    }

    &__errors-item {
        & + & {
            @include margin-level(top, 3XS);
        }
    }

    &__actions {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: var(--spacer-s);
        @include margin-level(top, S);
    }
}
</style>
